import { useMemo } from "react";
import { useAllMinisters, useInvalidateAllMinisters } from "./useAllMinisters";
import type { Minister } from "@/models/minister";

interface ScopeFilters {
  districtId?: string;
  regionId?: string;
}

/**
 * Hook for minister list data, scoped to a territory if provided.
 * All user-facing filtering, sorting, and pagination is handled client-side
 * inside MinistersTable via TanStack Table.
 *
 * @param scopeFilters - Locked territory scope (district/region from context).
 *   These are not user-controllable — they restrict which ministers are visible.
 */
export function useMinisterList(scopeFilters?: ScopeFilters) {
  const { data: allMinisters, isLoading } = useAllMinisters();
  const invalidate = useInvalidateAllMinisters();

  const ministers = useMemo((): Minister[] => {
    if (!allMinisters) return [];
    if (!scopeFilters?.districtId && !scopeFilters?.regionId) return allMinisters;

    return allMinisters.filter((m) => {
      if (scopeFilters.districtId && m.district_id !== scopeFilters.districtId) return false;
      if (scopeFilters.regionId && m.region_id !== scopeFilters.regionId) return false;
      return true;
    });
  }, [allMinisters, scopeFilters?.districtId, scopeFilters?.regionId]);

  return {
    ministers,
    isLoading,
    reload: invalidate,
  };
}
